import { Box } from "@mui/material";
import { TabLinks } from "components/TabLinks";
import InsightsUpsell from "components/UpsellLandingPages/InsightsUpsell";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useTenant } from "hooks/useTenant";
import { useMemo } from "react";
import { Navigate, Outlet, useMatch } from "react-router-dom";

import { LoadingState } from "./LoadingState";

export function Insights() {
  const { tenantId } = useTenant();
  useBreadcrumb({
    label: "Insights",
  });
  const match = useMatch({
    path: `/o/${tenantId}/insights/:tab`,
    end: false,
  });
  const { featureFlags: flags, loading: featureFlagsLoading } = useFeatureFlags();

  const tabs = useMemo(
    () => [
      {
        title: "Overview",
        to: "overview",
      },
      {
        title: "Benchmark",
        to: "benchmark",
      },
      {
        title: "ECHO",
        to: "echo",
      },
      {
        title: "RCRAInfo",
        to: "rcra-info",
      },
      {
        title: "NPDES",
        to: "npdes",
      },
      // Violations is a sub-feature of the Insights product, so it needs both
      // the HarmonizedViolations data pipeline and the InsightsUI purchase
      // gate. HarmonizedViolations alone is not enough — see the `requires`
      // annotation seeded for the flag. The InsightsUI half is redundant with
      // the early return below (this list is only ever consumed with the flag
      // on); kept deliberately so the rule reads where the tab is declared.
      ...(flags[FeatureFlagKey.HarmonizedViolations] && flags[FeatureFlagKey.InsightsUI]
        ? [
            {
              title: "Violations",
              to: "violations",
            },
          ]
        : []),
    ],
    [flags]
  );

  const currentTab = useMemo(
    () => tabs.find((t) => t.to === match?.params.tab),
    [match?.params.tab, tabs]
  );

  if (featureFlagsLoading) {
    return <LoadingState />;
  }

  // Insights is a purchased product: without InsightsUI every tab upsells,
  // including Violations. Checked before the redirect below so a tenant
  // without the product doesn't bounce through a tab route first.
  if (!flags[FeatureFlagKey.InsightsUI]) {
    return <InsightsUpsell />;
  }

  // Redirect to first tab if we don't have a match
  if (!currentTab) {
    return <Navigate to={tabs[0].to} />;
  }

  return (
    <Box>
      <TabLinks
        value={currentTab.to}
        tabs={tabs}
        aria-label="insights tabs nav"
        role="navigation"
      />
      <Outlet />
    </Box>
  );
}
